import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const sections = [
  {
    title: "1. Who We Are",
    content: `VicShield Global ("VicShield", "we", "us", "our") operates the VicShield enterprise cyber risk intelligence platform at vicshield.com. We are the data controller for personal data collected through the Platform.

Contact: support@vicshield.com
Website: vicshield.com

VicShield serves clients globally including in the European Union, United Kingdom, United States, Canada and other jurisdictions. This Privacy Policy applies to all clients regardless of location.`
  },
  {
    title: "2. Data We Collect",
    content: `We collect the following categories of data:

Account Data:
• Full name and corporate email address
• Company name and domain
• Subscription plan and billing information
• Account creation date and login history

Assessment Data:
• Cyber risk assessment answers (34 control points)
• Company profile information (sector, size, revenue, geography)
• Vendor information you register on the Platform
• Risk scores and financial exposure calculations generated from your answers

Technical Data:
• IP address and browser/device information
• Pages visited and features used within the Platform
• API key usage logs (Enterprise plan)
• Audit trail events (actions taken within your account)

We do not collect or store payment card details — these are handled directly by our payment processor.`
  },
  {
    title: "3. How We Use Your Data",
    content: `We use your data for the following purposes:

Service Delivery:
• Generating your cyber risk intelligence reports and dashboard
• Running Monte Carlo simulations and risk calculations
• Mapping your profile to applicable regulatory frameworks
• Scanning your domain for credential breaches (when credential scanning is configured)

Account Management:
• Verifying your identity and managing your subscription
• Sending account verification and password reset codes
• Responding to support requests

Platform Improvement:
• Understanding how features are used to improve the Platform
• Monitoring platform performance and security

Legal Compliance:
• Meeting our obligations under EU GDPR, UK GDPR and applicable data protection legislation
• Maintaining audit records as required

We do not use your data for advertising. VicShield products are completely ad-free.`
  },
  {
    title: "4. Legal Basis for Processing",
    content: `We process your personal data on the following legal bases under EU GDPR and UK GDPR:

• Contract Performance: Processing necessary to deliver the services you subscribed to
• Legitimate Interests: Platform security monitoring, fraud prevention, and service improvement
• Legal Obligation: Compliance with applicable laws and regulations
• Consent: Where we ask for your explicit consent for specific processing activities

You may withdraw consent at any time where consent is the legal basis for processing, without affecting the lawfulness of processing before withdrawal.

For US residents: We process your data based on our legitimate business interest in providing the services you have requested and our contractual obligations to you.`
  },
  {
    title: "5. Data Sharing",
    content: `We do not sell your data to any third party. Ever.

We share data only with carefully selected service providers necessary to deliver the Platform. These include:

• An AI provider — for generating AI-powered remediation narratives in audit reports. Only the minimum assessment data required to generate recommendations is shared. The provider does not retain or train on your data.
• A payment processor — for subscription billing. They receive billing information only, not assessment data.
• Cloud infrastructure providers — for hosting and data storage within the European Union.
• An email delivery provider — for sending transactional emails such as verification codes and notifications.
• A credential breach detection service — for scanning your corporate domain for known credential exposures, when this feature is configured. Domain data only is shared, protected by k-anonymity.

These providers operate under their own privacy policies and applicable data protection law, and access only the minimum data necessary for their specific function.

A full list of our data sub-processors is available on request at support@vicshield.com.

We may disclose data if required by law, court order, or regulatory authority in any jurisdiction where we operate.`
  },
  {
    title: "6. Credential Breach Scanning",
    content: `When credential breach scanning is configured, VicShield uses a third-party breach detection service to check whether email addresses associated with your corporate domain have appeared in known data breaches.

We use k-anonymity for all email searches — only an anonymous hash prefix is sent to the breach detection service. Your actual email addresses never leave VicShield's systems in readable form.

Domain-level monitoring registers your corporate domain with the breach detection service so VicShield is notified when new breaches containing your domain are detected. No individual employee data is shared beyond the domain name itself.

This feature activates only when credential scanning is configured. When not active, no data is sent to any breach detection service.`
  },
  {
    title: "7. Data Retention",
    content: `We retain your data for the following periods:

• Account data: Duration of subscription + 14 days after cancellation
• Assessment data: Duration of subscription + 14 days after cancellation
• Audit logs: 12 months from creation
• Payment records: 7 years (legal requirement in most jurisdictions)
• Backup data: Up to 30 days after deletion

After the retention period, data is permanently and irreversibly deleted from all our systems including backups.

You may request earlier deletion of your data by contacting support@vicshield.com, subject to our legal obligations to retain certain records such as payment records.`
  },
  {
    title: "8. Your Global Privacy Rights",
    content: `Depending on your location, you have the following rights regarding your personal data:

EU and UK Residents (EU GDPR and UK GDPR):
• Right of Access: Request a copy of the personal data we hold about you
• Right to Rectification: Request correction of inaccurate or incomplete data
• Right to Erasure: Request deletion of your personal data ("right to be forgotten")
• Right to Restriction: Request we restrict processing of your data
• Right to Data Portability: Receive your data in a structured, machine-readable format
• Right to Object: Object to processing based on legitimate interests
• Rights related to automated decision-making: Request human review of automated decisions

California Residents (CCPA/CPRA):
• Right to Know: Request disclosure of personal information we collect, use and share
• Right to Delete: Request deletion of your personal information
• Right to Correct: Request correction of inaccurate personal information
• Right to Opt-Out: We do not sell personal information — no opt-out is required
• Right to Non-Discrimination: We will not discriminate against you for exercising your rights

Canadian Residents (PIPEDA):
• Right to access your personal information
• Right to challenge accuracy and completeness
• Right to withdraw consent

All Users:
To exercise any of these rights, contact support@vicshield.com. We will respond within 30 days. We may need to verify your identity before processing requests.

Supervisory Authorities:
• EU (Lithuania — our operating base): State Data Protection Inspectorate — vdai.lrv.lt
• UK: Information Commissioner's Office — ico.org.uk
• You may also contact the supervisory authority in your own EU member state.`
  },
  {
    title: "9. Cookies",
    content: `VicShield uses the following types of cookies:

Essential Cookies:
• Authentication tokens stored in your browser — required for login functionality
• Cookie consent preference — stores your consent decision
• Session management — required for platform operation

These essential cookies cannot be disabled as they are necessary for the Platform to function.

We do not use advertising cookies, tracking cookies, or any third-party analytics cookies. We do not use Google Analytics or similar tracking services.`
  },
  {
    title: "10. Security",
    content: `We implement appropriate technical and organisational measures to protect your data including:

• TLS encryption for all data in transit between your browser and our servers
• Bcrypt password hashing — passwords are never stored in plain text
• Authentication token expiry with configurable session length
• API key authentication — keys are hashed before storage and never stored in plain text
• Role-based access controls limiting data access by function (Admin, Analyst, Viewer)
• Immutable audit logging of all significant account actions
• Data at rest encrypted by our EU-based cloud infrastructure provider
• Continuous background monitoring of active client domains
• Two-factor authentication (MFA) available to all plan tiers

However, no system is completely secure. You are responsible for maintaining the security of your account credentials. We strongly recommend enabling two-factor authentication on your account.`
  },
  {
    title: "11. Data Location and International Transfers",
    content: `VicShield processes and stores data as follows:

Data Storage:
Your personal data and assessment data are stored within the European Union on EU-based cloud infrastructure. Data does not leave the EU/EEA for storage purposes.

Frontend Application:
Our web application is served via a global content delivery network. Only static application files are served this way — no personal data is stored or processed at this layer.

Email Delivery:
Transactional emails are delivered via an EU-based email provider. Email delivery logs are retained subject to that provider's own privacy policy.

Third-Party Processing:
Certain platform features use third-party specialist services to deliver their full functionality. These providers process only the minimum data required for their specific function, do not retain your data after processing, and do not use your data for any purpose other than delivering the requested feature.

A full list of our data sub-processors including their locations is available on request at support@vicshield.com.

We will notify you at least 30 days in advance if our infrastructure changes in a way that materially affects where your data is processed.`
  },
  {
    title: "12. Children's Privacy",
    content: `VicShield is an enterprise B2B platform intended for business use only. We do not knowingly collect personal data from individuals under the age of 18. If you believe a minor has provided us with personal data, please contact support@vicshield.com and we will delete it promptly.`
  },
  {
    title: "13. Changes to This Policy",
    content: `We may update this Privacy Policy from time to time. We will notify you of material changes via email to your registered address at least 30 days before the changes take effect.

Continued use of the Platform after the effective date constitutes acceptance of the updated Privacy Policy.

If you have questions about any changes, contact support@vicshield.com.

Last updated: May 2026`
  },
];

function Section({ title, content }: { title: string; content: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-100 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left gap-4"
      >
        <span className="text-sm font-black text-slate-900 uppercase tracking-tight">
          {title}
        </span>
        <ChevronDown size={18} className={`text-slate-400 flex-shrink-0
          transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <p className="text-slate-500 text-sm font-medium leading-relaxed pb-5
              whitespace-pre-line">
              {content}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Privacy() {
  const navigate = useNavigate();
  return (
    <div className="bg-white font-['Inter'] min-h-screen">

      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <div onClick={() => navigate("/")}
            className="flex items-center gap-3 cursor-pointer group">
            <div className="w-9 h-9 bg-red-600 rounded-xl flex items-center justify-center
              shadow-lg shadow-red-200 group-hover:scale-105 transition-transform">
              <span className="text-white font-black text-base">V</span>
            </div>
            <span className="text-xl font-black tracking-tighter text-slate-900 uppercase">
              VicShield
            </span>
          </div>
          <button onClick={() => navigate(-1)}
            className="text-xs font-black uppercase tracking-widest text-slate-400
              hover:text-slate-900 transition-colors">
            ← Back
          </button>
        </div>
      </nav>

      {/* Hero */}
      <div className="bg-[#0f172a] py-16">
        <div className="max-w-4xl mx-auto px-6">
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-red-500 mb-3">
            Legal
          </p>
          <h1 className="text-4xl font-black text-white tracking-tighter uppercase mb-4">
            Privacy Policy
          </h1>
          <p className="text-slate-400 font-medium">
            Last updated: May 2026 · EU GDPR · UK GDPR · CCPA compliant
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-[2.5rem] border-2 border-slate-100 px-10 py-2
          shadow-sm mb-8">
          {sections.map((s, i) => (
            <Section key={i} title={s.title} content={s.content} />
          ))}
        </div>

        <div className="bg-[#0f172a] rounded-[2rem] p-8 text-center">
          <p className="text-slate-400 text-sm font-medium mb-2">
            Questions about your data or this policy?
          </p>
          <a href="mailto:support@vicshield.com"
            className="text-red-400 font-black text-sm hover:text-red-300 transition-colors">
            support@vicshield.com
          </a>
        </div>
      </div>

      <footer className="border-t border-slate-100 py-6 px-6">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
            © 2026 VicShield Global
          </span>
          <div className="flex gap-6">
            <button onClick={() => navigate("/terms")}
              className="text-[10px] font-black uppercase tracking-widest text-slate-400
                hover:text-red-500 transition-colors">
              Terms of Service
            </button>
            <button onClick={() => navigate("/pricing")}
              className="text-[10px] font-black uppercase tracking-widest text-slate-400
                hover:text-red-500 transition-colors">
              Pricing
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
